import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addMessage } from './store/chatSlice';
import { sendMessage } from './api/chat';

const Chat = ({ viewId, questionId, sessionId}) => {
  const [loading, setLoading] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const chatHistory = useSelector((state) => state.chat[viewId] || []);
  const dispatch = useDispatch();
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  const handleSendMessage = async () => {
    if (messageInput.trim() === '') return;
  
    setLoading(true);
  
    try {
      const response = await sendMessage(sessionId, questionId, messageInput);
      const result = response.data.data;
      
      // Add user's message with timestamp
      dispatch(addMessage({ 
        viewId, 
        message: messageInput, 
        sender: 'user',
        timestamp: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
      }));
  
      // Add system's response with timestamp
      dispatch(addMessage({ 
        viewId, 
        message: result, 
        sender: 'server',
        timestamp: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) 
      }));
  
    } catch (error) {
      console.error('Error sending message:', error);
      // Show error message
      dispatch(addMessage({ 
        viewId, 
        message: "Sorry, there was an error processing your request.", 
        sender: 'server',
        timestamp: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
      }));
    } finally {
      setLoading(false);
      setMessageInput('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };
  
  // Render empty state if no messages
  const renderEmptyState = () => {
    if (chatHistory.length === 0) {
      return (
        <div className="empty-chat">
          <p>No messages yet. Start a conversation!</p>
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className="chat-container">
      <div className="chat-messages">
        {renderEmptyState()}
        {chatHistory.map((message, index) => (
          <div
            key={index}
            className={`message ${message.sender === 'user' ? 'user' : 'system'}`}
          >
            <div className="avatar" style={{ 
              backgroundColor: message.sender === 'user' ? '#007aff' : '#e0e0e0',
              color: message.sender === 'user' ? 'white' : '#333',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>
              {message.sender === 'user' ? 'U' : 'AI'}
            </div>
            <div className="message-content">
              <div className="text">{message.text}</div>
              {message.timestamp && <div className="timestamp">{message.timestamp}</div>}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <div className="chat-input">
        <input
          type="text"
          placeholder="Type your message here..."
          value={messageInput}
          onChange={(e) => setMessageInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
        />
        <button onClick={handleSendMessage} disabled={loading}>
          {loading ? 'Sending...' : 'Send'}
        </button>
      </div>
      {loading && 
        <div className="chat-loader">
          <div className="loader"></div>
        </div>
      }
    </div>
  );
};

export default Chat;
